import { useCallback, useMemo, useState } from "react";
import {
  RiArrowLeftLine,
  RiArrowRightLine,
  RiCheckLine,
  RiDownload2Line,
  RiExternalLinkLine,
  RiFileCopyLine,
  RiFolderOpenLine,
  RiHardDrive3Line,
  RiSettings3Line,
  RiTerminalBoxLine,
} from "@remixicon/react";
import { Button } from "@/components/button";
import { Card, CardContent } from "@/components/card";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { cn } from "@/utils/utils";
import {
  ARCH_OPTIONS,
  DEFAULT_SETUP_CONFIG,
  DASHBOARD_URL,
  OS_OPTIONS,
  SETUP_UI_URL,
  type AutomaticSetupConfig,
  type LaunchMode,
  type NodeArch,
  type NodeOs,
  getLaunchPayload,
} from "@/components/silo/setup-node-types";

const STEPS = [
  { id: "platform", title: "Platform", caption: "OS and CPU" },
  { id: "storage", title: "Storage", caption: "Data folders" },
  { id: "options", title: "Options", caption: "Ports and updates" },
  { id: "launch", title: "Launch", caption: "Copy and run" },
] as const;

type DirectoryPickerWindow = Window & {
  showDirectoryPicker?: () => Promise<{ name: string }>;
};

function detectHostOs(): Pick<AutomaticSetupConfig, "os" | "arch" | "storagePath"> {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const platform =
    typeof navigator !== "undefined"
      ? (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
          ?.platform || navigator.platform || ""
      : "";
  const haystack = `${ua} ${platform}`.toLowerCase();

  if (haystack.includes("mac")) {
    const mac = OS_OPTIONS.find((o) => o.id === "macos")!;
    return { os: "macos", arch: mac.defaultArch, storagePath: mac.pathHint };
  }
  if (haystack.includes("win")) {
    const win = OS_OPTIONS.find((o) => o.id === "windows")!;
    return { os: "windows", arch: win.defaultArch, storagePath: win.pathHint };
  }
  const linux = OS_OPTIONS.find((o) => o.id === "linux")!;
  return { os: "linux", arch: linux.defaultArch, storagePath: linux.pathHint };
}

/** Web apps only get the folder name from the picker, never the absolute path. */
function suggestPathFromFolderName(
  os: NodeOs,
  folderName: string,
  currentPath: string,
): string {
  const safeName = folderName.replace(/[<>:"|?*]/g, "").trim() || "storage";
  const current = currentPath.trim();
  const defaultHints = OS_OPTIONS.map((o) => o.pathHint);
  const isDefaultHint = !current || defaultHints.includes(current);

  if (os === "windows") {
    if (!isDefaultHint && /^[a-zA-Z]:[\\/]/.test(current)) {
      const trimmed = current.replace(/[/\\]+$/, "");
      const parent = trimmed.replace(/[/\\][^/\\]+$/, "");
      return `${parent}\\${safeName}`;
    }
    return `C:\\${safeName}`;
  }

  if (!isDefaultHint && current.startsWith("/")) {
    const trimmed = current.replace(/\/+$/, "");
    const slash = trimmed.lastIndexOf("/");
    if (slash > 0) return `${trimmed.slice(0, slash)}/${safeName}`;
  }

  if (os === "macos") return `/Users/you/${safeName}`;
  return `/home/you/${safeName}`;
}

export function SetupNodeAutomatic() {
  const [stepIndex, setStepIndex] = useState(0);
  const [config, setConfig] = useState<AutomaticSetupConfig>(() => ({
    ...DEFAULT_SETUP_CONFIG,
    ...detectHostOs(),
  }));
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [pickerNote, setPickerNote] = useState<string | null>(null);

  const step = STEPS[stepIndex];
  const osMeta = OS_OPTIONS.find((o) => o.id === config.os) ?? OS_OPTIONS[0];
  const launch = useMemo(() => getLaunchPayload(config), [config]);

  const patch = useCallback((partial: Partial<AutomaticSetupConfig>) => {
    setConfig((prev) => ({ ...prev, ...partial }));
  }, []);

  const copyText = useCallback(async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((v) => (v === id ? null : v)), 2000);
    } catch {
      setCopiedId(null);
    }
  }, []);

  const downloadTextFile = useCallback((filename: string, content: string) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const selectOs = (os: NodeOs) => {
    const meta = OS_OPTIONS.find((o) => o.id === os);
    const defaultHints = OS_OPTIONS.map((o) => o.pathHint);
    const current = config.storagePath.trim();
    // Refresh the example path when empty or still one of our OS defaults.
    // Keep a custom path the user typed themselves.
    const shouldRefreshPath = !current || defaultHints.includes(current);

    patch({
      os,
      arch: meta?.defaultArch ?? "amd64",
      storagePath: shouldRefreshPath ? meta?.pathHint || "" : current,
      dataPath: shouldRefreshPath ? "" : config.dataPath,
    });
    if (shouldRefreshPath) setPickerNote(null);
  };

  const canContinue = () => {
    if (step.id === "storage") {
      return config.storagePath.trim().length > 0;
    }
    return true;
  };

  const goNext = () => {
    if (!canContinue()) return;
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const pickFolder = async () => {
    const w = window as DirectoryPickerWindow;
    if (!w.showDirectoryPicker) {
      setPickerNote(
        "Folder picker is not available in this browser. Paste the full path from Finder or your file manager.",
      );
      return;
    }
    try {
      const handle = await w.showDirectoryPicker();
      const folderName = handle.name?.trim() || "storage";

      // Browsers never expose the real absolute path (security). Build an
      // editable OS-specific suggestion from the folder name only.
      setConfig((prev) => {
        const suggested = suggestPathFromFolderName(prev.os, folderName, prev.storagePath);
        return { ...prev, storagePath: suggested };
      });
      setPickerNote(
        `Selected folder: "${folderName}". Browsers cannot read the real disk path - replace the example with your full path (macOS Finder: right-click folder, hold Option, Copy as Pathname).`,
      );
    } catch {
      // User cancelled the picker - keep current path
    }
  };

  return (
    <div className="space-y-6">
      <nav aria-label="Setup progress" className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {STEPS.map((s, index) => {
          const active = index === stepIndex;
          const done = index < stepIndex;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                if (index <= stepIndex) setStepIndex(index);
              }}
              className={cn(
                "text-left rounded-md border px-3 py-3 transition-colors",
                active && "border-[#FF8A3C] bg-[#FF8A3C]/10",
                done && !active && "border-[#FF8A3C]/40 bg-sidebar",
                !active && !done && "border-border bg-sidebar/60 text-muted-foreground",
              )}
            >
              <div className="flex items-center gap-2 text-xs font-medium">
                <span
                  className={cn(
                    "inline-flex size-5 items-center justify-center rounded-full border text-[10px]",
                    active || done
                      ? "border-[#FF8A3C] text-[#FF8A3C]"
                      : "border-border",
                  )}
                >
                  {done ? <RiCheckLine className="size-3" /> : index + 1}
                </span>
                {s.title}
              </div>
              <p className="mt-1 text-xs text-muted-foreground pl-7">{s.caption}</p>
            </button>
          );
        })}
      </nav>

      <Card className="bg-sidebar border-border rounded-md overflow-hidden">
        <CardContent className="p-6 md:p-8">
          {step.id === "platform" && (
            <PlatformStep
              config={config}
              onSelectOs={selectOs}
              onSelectArch={(arch) => patch({ arch })}
            />
          )}

          {step.id === "storage" && (
            <StorageStep
              config={config}
              pathHint={osMeta.pathHint}
              pickerNote={pickerNote}
              onPickFolder={() => void pickFolder()}
              onChangeStorage={(storagePath) => patch({ storagePath })}
              onChangeData={(dataPath) => patch({ dataPath })}
            />
          )}

          {step.id === "options" && (
            <OptionsStep config={config} onChange={patch} />
          )}

          {step.id === "launch" && (
            <LaunchStep
              config={config}
              launch={launch}
              copiedId={copiedId}
              onCopy={copyText}
              onDownload={downloadTextFile}
            />
          )}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={stepIndex === 0}
              className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
            >
              <RiArrowLeftLine className="size-4" />
              Back
            </Button>

            {step.id !== "launch" ? (
              <Button
                type="button"
                onClick={goNext}
                disabled={!canContinue()}
                className="bg-[#FF8A3C] text-black hover:bg-[#FFA96B]"
              >
                Continue
                <RiArrowRightLine className="size-4" />
              </Button>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="outline" className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]">
                  <a href={SETUP_UI_URL} target="_blank" rel="noopener noreferrer">
                    Open setup UI
                    <RiExternalLinkLine className="size-4" />
                  </a>
                </Button>
                <Button asChild className="bg-[#FF8A3C] text-black hover:bg-[#FFA96B]">
                  <a href={DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
                    Open dashboard
                    <RiExternalLinkLine className="size-4" />
                  </a>
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PlatformStep({
  config,
  onSelectOs,
  onSelectArch,
}: {
  config: AutomaticSetupConfig;
  onSelectOs: (os: NodeOs) => void;
  onSelectArch: (arch: NodeArch) => void;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold">Choose your operating system</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Pick the host where Docker will run the Silo node. Architecture defaults to a safe choice for that OS.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {OS_OPTIONS.map((os) => {
          const selected = config.os === os.id;
          return (
            <button
              key={os.id}
              type="button"
              onClick={() => onSelectOs(os.id)}
              className={cn(
                "rounded-md border p-4 text-left transition-colors active:scale-[0.99]",
                selected
                  ? "border-[#FF8A3C] bg-[#FF8A3C]/10"
                  : "border-border bg-background/40 hover:border-[#FF8A3C]/50",
              )}
            >
              <div className="font-medium">{os.label}</div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {os.description}
              </p>
            </button>
          );
        })}
      </div>

      <div>
        <h3 className="text-base font-medium mb-3">CPU architecture</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
          {ARCH_OPTIONS.map((arch) => {
            const selected = config.arch === arch.id;
            return (
              <button
                key={arch.id}
                type="button"
                onClick={() => onSelectArch(arch.id)}
                className={cn(
                  "rounded-md border p-4 text-left transition-colors active:scale-[0.99]",
                  selected
                    ? "border-[#FF8A3C] bg-[#FF8A3C]/10"
                    : "border-border bg-background/40 hover:border-[#FF8A3C]/50",
                )}
              >
                <div className="font-mono text-sm font-medium">{arch.label}</div>
                <p className="text-xs text-muted-foreground mt-1">{arch.hint}</p>
              </button>
            );
          })}
        </div>
        {config.os === "macos" && config.arch === "arm64" && (
          <p className="text-xs text-[#FFD3B3] mt-3">
            Apple Silicon detected as default. Use amd64 only for Intel Macs.
          </p>
        )}
      </div>
    </div>
  );
}

function StorageStep({
  config,
  pathHint,
  pickerNote,
  onPickFolder,
  onChangeStorage,
  onChangeData,
}: {
  config: AutomaticSetupConfig;
  pathHint: string;
  pickerNote: string | null;
  onPickFolder: () => void;
  onChangeStorage: (value: string) => void;
  onChangeData: (value: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Choose storage location</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          This host path holds TrustKey material and node data. Use a drive with at least 500 GB free.
        </p>
      </div>

      <div className="space-y-2 max-w-2xl">
        <Label htmlFor="storage-path">Storage folder (host path)</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            id="storage-path"
            value={config.storagePath}
            onChange={(e) => onChangeStorage(e.target.value)}
            placeholder={pathHint}
            className="font-mono text-sm"
            aria-describedby="storage-help"
            autoComplete="off"
            spellCheck={false}
          />
          <Button
            type="button"
            variant="outline"
            onClick={onPickFolder}
            className="shrink-0 hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
          >
            <RiFolderOpenLine className="size-4" />
            Browse
          </Button>
        </div>
        <p id="storage-help" className="text-xs text-muted-foreground">
          Paste the full absolute path Docker should mount. Example: {pathHint}.
        </p>
        {pickerNote && (
          <p className="text-xs text-[#FFD3B3] leading-relaxed rounded-md border border-[#FF8A3C]/30 bg-[#FF8A3C]/10 px-3 py-2">
            {pickerNote}
          </p>
        )}
      </div>

      <div className="space-y-2 max-w-2xl">
        <Label htmlFor="data-path">Wizard data folder (optional)</Label>
        <Input
          id="data-path"
          value={config.dataPath}
          onChange={(e) => onChangeData(e.target.value)}
          placeholder="Auto: sibling data folder next to storage"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Leave blank to keep wizard state beside your storage path.
        </p>
      </div>

      <div className="rounded-md border border-border bg-background/30 p-4 max-w-2xl flex gap-3">
        <RiHardDrive3Line className="size-5 text-[#FF8A3C] shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground space-y-1">
          <p>
            Also plan for: public IP or DDNS, router forwards for the node TCP/UDP port you choose in the next step, and a Larissa wallet for payouts.
          </p>
        </div>
      </div>
    </div>
  );
}

function OptionsStep({
  config,
  onChange,
}: {
  config: AutomaticSetupConfig;
  onChange: (partial: Partial<AutomaticSetupConfig>) => void;
}) {
  const webPort = config.setupUiPort.trim() || "8080";
  const consolePort = config.dashboardPort.trim() || "14002";
  const nodePort = config.nodePort.trim() || "28967";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Ports and updates</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Ports stay editable for multiple nodes on one host. Compose uses{" "}
          <span className="font-mono text-foreground">${"{WEB_PORT:-8080}"}</span> style
          (env first, then default). Your choices are exported before run so publish, container, and
          setup UI stay 1:1.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
        <div className="space-y-2">
          <Label htmlFor="setup-port">Setup UI (WEB_PORT)</Label>
          <Input
            id="setup-port"
            value={config.setupUiPort}
            onChange={(e) => onChange({ setupUiPort: e.target.value.replace(/\D/g, "").slice(0, 5) })}
            className="font-mono"
            inputMode="numeric"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="dash-port">Console (SILO_CONSOLE_PORT)</Label>
          <Input
            id="dash-port"
            value={config.dashboardPort}
            onChange={(e) => onChange({ dashboardPort: e.target.value.replace(/\D/g, "").slice(0, 5) })}
            className="font-mono"
            inputMode="numeric"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="node-port">Node TCP/UDP</Label>
          <Input
            id="node-port"
            value={config.nodePort}
            onChange={(e) => onChange({ nodePort: e.target.value.replace(/\D/g, "").slice(0, 5) })}
            className="font-mono"
            inputMode="numeric"
          />
        </div>
      </div>

      <div className="rounded-md border border-[#FF8A3C]/30 bg-[#FF8A3C]/10 p-4 max-w-3xl space-y-2 text-sm text-[#FFD3B3]">
        <p className="font-medium text-[#FF8A3C]">How ports are applied</p>
        <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
          <li>
            Compose ports:{" "}
            <span className="font-mono text-foreground text-xs">
              {"${WEB_PORT:-8080}:${WEB_PORT:-8080}"}
            </span>{" "}
            (same pattern for console and node)
          </li>
          <li>
            This wizard exports{" "}
            <span className="font-mono text-foreground">
              WEB_PORT={webPort}
            </span>
            ,{" "}
            <span className="font-mono text-foreground">
              SILO_CONSOLE_PORT={consolePort}
            </span>
            ,{" "}
            <span className="font-mono text-foreground">
              SILO_NODE_TCP_PORT={nodePort}
            </span>{" "}
            before start
          </li>
          <li>
            Router must forward TCP/UDP <span className="font-mono text-foreground">{nodePort}</span>
          </li>
          <li>
            Second node: change the exported ports (e.g. 8081 / 14003 / 28968) and use a separate storage folder
          </li>
        </ul>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
        <div className="space-y-2">
          <Label htmlFor="image-tag">Image tag</Label>
          <Input
            id="image-tag"
            value={config.imageTag}
            onChange={(e) => onChange({ imageTag: e.target.value })}
            className="font-mono"
          />
        </div>
        <div className="space-y-2">
          <Label>Auto update</Label>
          <div className="flex gap-2">
            {[true, false].map((value) => (
              <button
                key={String(value)}
                type="button"
                onClick={() => onChange({ autoUpdate: value })}
                className={cn(
                  "flex-1 rounded-md border px-3 py-2 text-sm transition-colors",
                  config.autoUpdate === value
                    ? "border-[#FF8A3C] bg-[#FF8A3C]/10 text-[#FF8A3C]"
                    : "border-border hover:border-[#FF8A3C]/50",
                )}
              >
                {value ? "On" : "Off"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-md border border-border bg-background/30 p-4 max-w-3xl flex gap-3">
        <RiSettings3Line className="size-5 text-[#FF8A3C] shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">
          After start, open setup UI at{" "}
          <span className="font-mono text-foreground">http://127.0.0.1:{webPort}</span>. That UI will
          require router forwards for node port{" "}
          <span className="font-mono text-foreground">{nodePort}</span> only.
        </p>
      </div>
    </div>
  );
}

function LaunchStep({
  config,
  launch,
  copiedId,
  onCopy,
  onDownload,
}: {
  config: AutomaticSetupConfig;
  launch: ReturnType<typeof getLaunchPayload>;
  copiedId: string | null;
  onCopy: (id: string, text: string) => void;
  onDownload: (filename: string, content: string) => void;
}) {
  const [mode, setMode] = useState<LaunchMode>("inline");

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Run your node</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Same two options on every platform: paste an inline CLI, or download compose files and run a short CLI.
        </p>
      </div>

      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <SummaryItem label="OS" value={config.os} />
        <SummaryItem label="Arch" value={config.arch} />
        <SummaryItem label="Storage" value={config.storagePath || "-"} mono />
        <SummaryItem label="Auto update" value={config.autoUpdate ? "on" : "off"} />
        <SummaryItem label="WEB_PORT" value={config.setupUiPort || "8080"} mono />
        <SummaryItem label="SILO_CONSOLE_PORT" value={config.dashboardPort || "14002"} mono />
        <SummaryItem label="Node TCP/UDP" value={config.nodePort || "28967"} mono />
        <SummaryItem
          label="Router forward"
          value={`${config.nodePort || "28967"} TCP + UDP`}
          mono
        />
      </dl>

      <Tabs
        value={mode}
        onValueChange={(v) => setMode(v as LaunchMode)}
        className="w-full"
      >
        <TabsList className="mb-4">
          <TabsTrigger value="inline">CLI command</TabsTrigger>
          <TabsTrigger value="files">Download files + CLI</TabsTrigger>
        </TabsList>

        <TabsContent value="inline" className="mt-0 space-y-3">
          <p className="text-sm text-muted-foreground">{launch.inline.hint}</p>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="inline-flex items-center gap-2">
              <RiTerminalBoxLine className="size-4 text-[#FF8A3C]" />
              {launch.inline.label}
            </Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onCopy("inline", launch.inline.content)}
              className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
            >
              {copiedId === "inline" ? (
                <RiCheckLine className="size-4" />
              ) : (
                <RiFileCopyLine className="size-4" />
              )}
              {copiedId === "inline" ? "Copied" : "Copy"}
            </Button>
          </div>
          <pre className="overflow-x-auto rounded-md border border-border bg-zinc-950/80 p-4 text-xs leading-relaxed text-zinc-100 font-mono max-h-[28rem]">
            {launch.inline.content}
          </pre>
        </TabsContent>

        <TabsContent value="files" className="mt-0 space-y-4">
          <p className="text-sm text-muted-foreground">{launch.files.hint}</p>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                onDownload(launch.files.composeFileName, launch.files.composeYml)
              }
              className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
            >
              <RiDownload2Line className="size-4" />
              Download {launch.files.composeFileName}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onDownload(launch.files.envFileName, launch.files.envFile)}
              className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
            >
              <RiDownload2Line className="size-4" />
              Download {launch.files.envFileName}
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="inline-flex items-center gap-2">
                <RiTerminalBoxLine className="size-4 text-[#FF8A3C]" />
                {launch.files.label}
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onCopy("files-cli", launch.files.content)}
                className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
              >
                {copiedId === "files-cli" ? (
                  <RiCheckLine className="size-4" />
                ) : (
                  <RiFileCopyLine className="size-4" />
                )}
                {copiedId === "files-cli" ? "Copied" : "Copy"}
              </Button>
            </div>
            <pre className="overflow-x-auto rounded-md border border-border bg-zinc-950/80 p-4 text-xs leading-relaxed text-zinc-100 font-mono">
              {launch.files.content}
            </pre>
          </div>

          <details className="rounded-md border border-border bg-background/30 p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Preview file contents
            </summary>
            <div className="mt-4 space-y-4">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-mono text-muted-foreground">
                    {launch.files.composeFileName}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onCopy("compose-yml", launch.files.composeYml)}
                    className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
                  >
                    {copiedId === "compose-yml" ? "Copied" : "Copy"}
                  </Button>
                </div>
                <pre className="overflow-x-auto rounded-md border border-border bg-zinc-950/80 p-3 text-xs text-zinc-100 font-mono max-h-48">
                  {launch.files.composeYml}
                </pre>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-mono text-muted-foreground">
                    {launch.files.envFileName}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onCopy("env-file", launch.files.envFile)}
                    className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
                  >
                    {copiedId === "env-file" ? "Copied" : "Copy"}
                  </Button>
                </div>
                <pre className="overflow-x-auto rounded-md border border-border bg-zinc-950/80 p-3 text-xs text-zinc-100 font-mono max-h-40">
                  {launch.files.envFile}
                </pre>
              </div>
            </div>
          </details>
        </TabsContent>
      </Tabs>

      <ol className="space-y-2 text-sm text-muted-foreground list-decimal pl-5 max-w-2xl">
        <li>Ensure Docker is installed and running on the host.</li>
        <li>Use either the inline CLI, or download the files and run the short CLI.</li>
        <li>
          Open setup UI at{" "}
          <a
            className="text-[#FF8A3C] hover:underline"
            href={`http://127.0.0.1:${config.setupUiPort || "8080"}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            http://127.0.0.1:{config.setupUiPort || "8080"}
          </a>
        </li>
        <li>
          After setup, monitor the node at{" "}
          <a
            className="text-[#FF8A3C] hover:underline"
            href={`http://127.0.0.1:${config.dashboardPort || "14002"}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            http://127.0.0.1:{config.dashboardPort || "14002"}
          </a>
        </li>
      </ol>
    </div>
  );
}

function SummaryItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-background/30 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("mt-1 truncate", mono && "font-mono text-xs")}>{value}</dd>
    </div>
  );
}
